import Button from "@edaz/edazkit/Button/index";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import type { ViewComponent } from "@frui.ts/views";
import { registerView, View } from "@frui.ts/views";
import HeaderWithBackButton from "components/headerWithBackButton";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { observer, Observer } from "mobx-react-lite";
import React from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { formatAmount, formatDate } from "utils/localizations";
import PairingByPaymentDetailViewModel from "viewModels/finance/paymentsPairing/pairingByPayment/pairingByPaymentDetailViewModel";
import type TransferType from "entities/transferType";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import type SenderBankAccount from "entities/refund/senderBankAccount";
import UnpairedPaymentStatus from "entities/unpairedPaymentStatus";

const PairingByPaymentDetailView: ViewComponent<PairingByPaymentDetailViewModel> = observer(({ vm }) => {
  let actionButton;
  if (vm.item && vm.item.status === UnpairedPaymentStatus.New && vm.item.isRefundable && vm.allowToRefund) {
    actionButton = (
      <Button variant="primary" className="rounded" onClick={vm.onCreateRefundClick}>
        {vm.translate("createRefund")}
      </Button>
    );
  }

  return (
    <>
      <div className="bg-light full-width mt-n7 pt-4 rounded-top">
        <LoaderOverlay loading={vm.busyWatcher.isBusy}>
          <HeaderWithBackButton
            title={vm.translate("matchingByPaymentTitle")}
            back={{ link: vm.backLink, action: vm.close }}
            className="mb-4"
            actionButton={actionButton}
          />
          {!vm.isInitialized || !vm.item ? (
            <div style={{ minHeight: "100px" }} />
          ) : (
            <>
              <div className="row">
                <Form.Group as={Col} md={6} lg={4} xl={3}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "senderName")}</Form.Label>
                  <FakeInput target={vm.item} property="senderName" />
                </Form.Group>
                <Form.Group as={Col} md={6} lg={8} xl={6}>
                  <Form.Label>{vm.translateAttribute("senderBankAccount", "accountNumber")}</Form.Label>
                  <FakeInput
                    target={vm.item}
                    property="senderBankAccount"
                    valueFormatter={(bankAccount: SenderBankAccount) => formatBankAccount(bankAccount)}
                  />
                </Form.Group>
                <Form.Group as={Col} sm={6} md={6} lg={4} xl={3}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "variableSymbol")}</Form.Label>
                  <FakeInput target={vm.item} property="variableSymbol" />
                </Form.Group>
                <div className="col col-sm-6 col-lg-4 col-xl-3">
                  <div className="row">
                    <Form.Group as={Col} sm={6}>
                      <Form.Label>{vm.translateAttribute("unpairedPayment", "constantSymbol")}</Form.Label>
                      <FakeInput target={vm.item} property="constantSymbol" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6}>
                      <Form.Label>{vm.translateAttribute("unpairedPayment", "specificSymbol")}</Form.Label>
                      <FakeInput target={vm.item} property="specificSymbol" />
                    </Form.Group>
                  </div>
                </div>
                <Form.Group as={Col} sm={6} md={3} lg={4} xl={3}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "paymentType")}</Form.Label>
                  <FakeInput
                    target={vm.item}
                    property="paymentType"
                    valueFormatter={(paymentType: TransferType) => vm.transferPaymentTypeName(paymentType)}
                  />
                </Form.Group>
                <Form.Group as={Col} sm={6} md={3} lg={4} xl={3}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "paidAmount")}</Form.Label>
                  <FakeInput
                    target={vm.item}
                    property="paidAmount"
                    valueFormatter={(amount: number) => formatAmount(amount)}
                    className="text-nowrap"
                  />
                </Form.Group>
                <Form.Group as={Col} sm={6} lg={4} xl={3}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "postingDate")}</Form.Label>
                  <FakeInput
                    target={vm.item}
                    property="postingDate"
                    valueFormatter={(date?: Date) => formatDate(date)}
                    className="text-nowrap"
                  />
                </Form.Group>
                <Form.Group as={Col} md={6}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "senderAddress")}</Form.Label>
                  <FakeInput target={vm.item} property="senderAddress" />
                </Form.Group>
                <Form.Group as={Col} md={6}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "messageForRecipient")}</Form.Label>
                  <FakeInput target={vm.item} property="messageForRecipient" />
                </Form.Group>
              </div>
              <hr className="mt-0 mb-4" />
              <div className="row">
                <Form.Group as={Col} md={6} lg={6} xl={6} xxl={3}>
                  <Form.Label>{vm.translateAttribute("recipientBankAccount", "accountNumber")}</Form.Label>
                  <FakeInput
                    target={vm.item.recipientBankAccount}
                    property="accountNumber"
                    valueFormatter={(accountNumber: string) => formatBankAccount({ accountNumber })}
                  />
                </Form.Group>
                <Form.Group as={Col} md={6} lg={6} xl={6} xxl={3}>
                  <Form.Label>{vm.translateAttribute("recipientBankAccount", "iban")}</Form.Label>
                  <FakeInput target={vm.item.recipientBankAccount} property="iban" />
                </Form.Group>
                <Form.Group as={Col} md={6} lg={6} xl={6} xxl={3}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "bankTransactionId")}</Form.Label>
                  <FakeInput target={vm.item} property="bankTransactionId" />
                </Form.Group>
                <Form.Group as={Col} md={6} lg={6} xl={6} xxl={3}>
                  <Form.Label>{vm.translateAttribute("unpairedPayment", "externalBankTransactionId")}</Form.Label>
                  <FakeInput target={vm.item} property="externalBankTransactionId" />
                </Form.Group>
              </div>
            </>
          )}
        </LoaderOverlay>
      </div>
      {vm.isInitialized && vm.item.isPairable && <Observer>{() => <View vm={vm.activeChild} />}</Observer>}
    </>
  );
});

registerView(PairingByPaymentDetailView, PairingByPaymentDetailViewModel);
